import { useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, FileText, Trash2, X, ZoomIn } from "lucide-react";
import UploadDropzone from "../UploadDropzone";
import { Button } from "../ui/button";
import { uploadIdentityDocument } from "../../api/shared";

type DocumentUploadFieldProps = {
  label: string;
  folderName: string;
  /** Nom suggéré sans extension, ex. "carte_cip_1072408380" (voir doc bucket). */
  filename?: string;
  currentUrl: string | null;
  /** Verrouillé (dossier déjà validé) — lecture seule, pas de formulaire. */
  readOnly?: boolean;
  onSubmitted: (url: string) => Promise<{ ok: true } | { ok: false; message: string }>;
};

type FileKind = "image" | "pdf" | "other";

/**
 * Les URL "capacité" du bucket (`find-file?filename=xxx.pdf&...&secret_key=...`) ne se terminent
 * jamais par l'extension — elle se trouve dans le paramètre `filename`. On la lit en priorité,
 * avec repli sur le nom/chemin brut (fichier local sélectionné, ou URL classique).
 */
function extensionOf(nameOrUrl: string): string {
  try {
    const parsed = new URL(nameOrUrl);
    const filename = parsed.searchParams.get("filename");
    if (filename) {
      const match = /\.([a-z0-9]+)$/i.exec(filename);
      if (match) return match[1].toLowerCase();
    }
  } catch {
    // Pas une URL absolue (ex. nom de fichier local) — on continue avec le chemin brut.
  }
  const path = nameOrUrl.split("?")[0].toLowerCase();
  const match = /\.([a-z0-9]+)$/i.exec(path);
  return match ? match[1] : "";
}

function detectKind(nameOrUrl: string, mimeType?: string): FileKind {
  if (mimeType?.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "pdf";
  const ext = extensionOf(nameOrUrl);
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "other";
}

function FilePreview({
  src,
  kind,
  label,
  onZoom,
}: {
  src: string;
  kind: FileKind;
  label: string;
  onZoom?: () => void;
}) {
  if (kind === "image") {
    return (
      <button
        type="button"
        onClick={onZoom}
        className="group relative block w-full cursor-zoom-in"
        aria-label={`Agrandir ${label}`}
      >
        <img
          src={src}
          alt={label}
          className="h-40 w-full rounded-[8px] border bg-white object-contain"
          style={{ borderColor: "#E5E7EB" }}
        />
        <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded bg-black/60 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
          <ZoomIn size={14} /> Zoomer
        </div>
      </button>
    );
  }
  if (kind === "pdf") {
    return (
      <embed
        src={src}
        type="application/pdf"
        className="h-40 w-full rounded-[8px] border"
        style={{ borderColor: "#E5E7EB" }}
      />
    );
  }
  return (
    <div
      className="flex h-40 w-full items-center justify-center rounded-[8px] border bg-[#F3F4F4]"
      style={{ borderColor: "#E5E7EB" }}
    >
      <FileText size={32} className="text-gray-400" />
    </div>
  );
}

export default function DocumentUploadField({
  label,
  folderName,
  filename,
  currentUrl,
  readOnly,
  onSubmitted,
}: DocumentUploadFieldProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const onSend = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    const uploaded = await uploadIdentityDocument(file, folderName, filename);
    if (!uploaded.ok) {
      setError(uploaded.message);
      setBusy(false);
      return;
    }
    const submitted = await onSubmitted(uploaded.url);
    setBusy(false);
    if (!submitted.ok) {
      setError(submitted.message);
      return;
    }
    setFile(null);
  };

  const zoomOverlay = zoomed && (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-6"
      onClick={() => setZoomed(null)}
    >
      <button
        type="button"
        onClick={() => setZoomed(null)}
        aria-label="Fermer"
        className="absolute right-4 top-4 text-white/80 hover:text-white"
      >
        <X size={24} />
      </button>
      <img src={zoomed} alt={label} className="max-h-full max-w-full cursor-zoom-out object-contain" />
    </div>
  );

  if (readOnly) {
    return (
      <div className="rounded-[10px] border bg-[#F3F4F4] p-3" style={{ borderColor: "#E5E7EB" }}>
        <div className="flex items-center gap-2 text-sm font-semibold text-[#0B1F16]">
          <CheckCircle2 size={16} className="text-[#00553E]" />
          {label}
        </div>
        {currentUrl && (
          <div className="mt-2 space-y-2">
            <FilePreview
              src={currentUrl}
              kind={detectKind(currentUrl)}
              label={label}
              onZoom={() => setZoomed(currentUrl)}
            />
            <a
              href={currentUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[#00553E] hover:underline"
            >
              Voir le document <ExternalLink size={14} />
            </a>
          </div>
        )}
        {zoomOverlay}
      </div>
    );
  }

  return (
    <div>
      <div className="font-semibold mb-1">{label}</div>

      {file && previewUrl ? (
        <div className="space-y-2">
          <FilePreview
            src={previewUrl}
            kind={detectKind(file.name, file.type)}
            label={label}
            onZoom={() => setZoomed(previewUrl)}
          />
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="truncate text-gray-600">
              {file.name} ({Math.round(file.size / 1024)} ko)
            </span>
            <button
              type="button"
              onClick={() => setFile(null)}
              className="shrink-0 text-red-600 hover:text-red-700"
              title="Retirer ce fichier"
              aria-label="Retirer ce fichier"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ) : (
        <>
          {currentUrl && (
            <div className="mb-3 space-y-2">
              <FilePreview
                src={currentUrl}
                kind={detectKind(currentUrl)}
                label={label}
                onZoom={() => setZoomed(currentUrl)}
              />
              <a
                href={currentUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-[#00553E] hover:underline"
              >
                Document actuel <ExternalLink size={14} />
              </a>
            </div>
          )}
          <UploadDropzone onFiles={(files) => setFile(files[0] ?? null)} />
        </>
      )}

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      <Button type="button" size="sm" className="mt-3" disabled={!file || busy} onClick={() => void onSend()}>
        {busy ? "Envoi…" : "Envoyer"}
      </Button>
      {zoomOverlay}
    </div>
  );
}
